/* eslint-disable @typescript-eslint/no-useless-constructor */
import { SupabaseClient } from '@supabase/supabase-js';
import { HomeworkStudentProfile, HomeworkType } from '@grcs/types/DataTypes';
import { BaseRepository } from '../BaseRepository';
import { DatabaseArrayResult, TABLE_NAMES } from '../types';

/**
 * Homework repository that handles all homework-related database operations
 * Preserves all existing function signatures and business logic
 */
export class HomeworkRepository extends BaseRepository {
  constructor(supabase: SupabaseClient<any, 'public', any>) {
    super(supabase);
  }

  /**
   * Preserves exact logic from getHomeworkByClassId
   */
  async getHomeworkByClassId(
    classId: string,
  ): Promise<DatabaseArrayResult<HomeworkType>> {
    if (!classId) throw new Error('Class id is not defined');
    return this.getDataById(TABLE_NAMES.HOMEWORK, 'class_token', classId);
  }

  /**
   * Preserves exact logic from getHomeworksByStudentId
   */
  async getHomeworksByStudentId(
    studentId: string,
  ): Promise<DatabaseArrayResult<HomeworkStudentProfile>> {
    if (!studentId) throw new Error('Student id is not defined');
    return this.getDataById(
      TABLE_NAMES.HOMEWORK_STUDENT_RELATION,
      'student_token',
      studentId,
      true,
    );
  }

  /**
   * Preserves exact logic from getAssignedHomeworkByStudentIdAndHomeworkId
   */
  async getAssignedHomeworkByStudentIdAndHomeworkId(
    studentId: string,
    homeworkId: string,
  ): Promise<DatabaseArrayResult<HomeworkStudentProfile>> {
    if (!studentId) throw new Error('Student id is not defined');
    if (!homeworkId) throw new Error('Homework id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .select('*')
        .eq('student_token', studentId)
        .eq('homework_token', homeworkId);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from getAllHomeworksByArticleId
   */
  async getAllHomeworksByArticleId(
    articleId: string,
  ): Promise<DatabaseArrayResult<HomeworkStudentProfile>> {
    if (!articleId) throw new Error('Article id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .select('*')
        .eq('article_token', articleId);

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from getAllHomeworksByStudentId
   */
  async getAllHomeworksByStudentId(
    studentId: string,
  ): Promise<DatabaseArrayResult<HomeworkStudentProfile>> {
    if (!studentId) throw new Error('Student id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .select('*')
        .eq('student_token', studentId);

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from getAllHomeworksByClassId
   */
  async getAllHomeworksByClassId(
    classId: string,
  ): Promise<DatabaseArrayResult<HomeworkStudentProfile>> {
    if (!classId) throw new Error('Class id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .select('*')
        .eq('class_token', classId);

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  async getAllHomeworksByStudentIdAndClassId(
    studentId: string,
    classId: string,
  ): Promise<DatabaseArrayResult<HomeworkStudentProfile>> {
    if (!studentId) throw new Error('Student id is not defined');
    if (!classId) throw new Error('Class id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .select('*')
        .eq('student_token', studentId)
        .eq('class_token', classId);

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from getAllStudentsByHomeworkIdAndClassId
   */
  async getAllStudentsByHomeworkIdAndClassId(
    homeworkId: string,
    classId: string,
  ): Promise<DatabaseArrayResult<HomeworkStudentProfile>> {
    if (!classId) throw new Error('Class id is not defined');
    if (!homeworkId) throw new Error('Homework id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .select('*')
        .eq('class_token', classId)
        .eq('homework_token', homeworkId);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from updateViewTimesByStudentIdAndHomeworkId
   */
  async updateViewTimesByStudentIdAndHomeworkId(
    studentId: string,
    homeworkId: string,
    totalTime: number,
  ): Promise<DatabaseArrayResult<any>> {
    if (!studentId) throw new Error('Student id is not defined');
    if (!homeworkId) throw new Error('Homework id is not defined');

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .update([{ total_time: totalTime }])
        .eq('student_token', studentId)
        .eq('homework_token', homeworkId);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Get homeworks by multiple homework tokens (for complex queries)
   */
  async getHomeworksByTokens(
    homeworkTokens: string[],
  ): Promise<DatabaseArrayResult<any>> {
    if (!homeworkTokens.length) return { data: [], error: null };

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.HOMEWORK)
        .select()
        .in('homework_token', homeworkTokens);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Get articles by multiple article tokens (for complex queries)
   */
  async getArticlesByTokens(
    articleTokens: string[],
  ): Promise<DatabaseArrayResult<any>> {
    if (!articleTokens.length) return { data: [], error: null };

    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.LIBRARY)
        .select('article, title')
        .in('id', articleTokens);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Initialize homework assignments for a newly enrolled student
   * Gets all existing homework for the student's class and creates assignments
   */
  async initializeHomeworkForStudent(
    studentToken: string,
    classToken: string,
    studentFullName: string,
  ): Promise<DatabaseArrayResult<any>> {
    if (!studentToken) throw new Error('Student token is required');
    if (!classToken) throw new Error('Class token is required');

    try {
      console.log(
        `🎯 Initializing homework for student ${studentToken} in class ${classToken}`,
      );

      // 1. Get all existing homework assignments for this class
      const { data: existingHomework, error: homeworkError } =
        await this.supabase
          .from(TABLE_NAMES.HOMEWORK)
          .select('homework_token, article_token, title, grade, chapter')
          .eq('class_token', classToken)
          .eq('deleted', false);

      if (homeworkError) {
        console.error('Error fetching existing homework:', homeworkError);
        return { data: null, error: homeworkError };
      }

      if (!existingHomework || existingHomework.length === 0) {
        console.log('ℹ️ No existing homework found for this class');
        return { data: [], error: null };
      }

      console.log(
        `📚 Found ${existingHomework.length} homework assignments for class`,
      );

      // 2. Check which homework assignments the student already has (avoid duplicates)
      const { data: existingAssignments, error: assignmentError } =
        await this.supabase
          .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
          .select('homework_token')
          .eq('student_token', studentToken)
          .eq('class_token', classToken);

      if (assignmentError) {
        console.error('Error checking existing assignments:', assignmentError);
        return { data: null, error: assignmentError };
      }

      const existingHomeworkTokens = new Set(
        existingAssignments?.map(
          (assignment: any) => assignment.homework_token,
        ) || [],
      );

      // 3. Filter out homework the student already has
      const newHomeworkAssignments = existingHomework.filter(
        (homework: any) => !existingHomeworkTokens.has(homework.homework_token),
      );

      if (newHomeworkAssignments.length === 0) {
        console.log(
          'ℹ️ Student already has all homework assignments for this class',
        );
        return { data: [], error: null };
      }

      console.log(
        `✨ Creating ${newHomeworkAssignments.length} new homework assignments for student`,
      );

      // 4. Create homework-student relationships for new assignments
      const relationshipData = newHomeworkAssignments.map((homework: any) => ({
        homework_token: homework.homework_token,
        student_token: studentToken,
        class_token: classToken,
        article_token: homework.article_token,
        created_at: new Date().toISOString(),
        completed: false,
        full_name: studentFullName,
        // Initialize with default progress
        total_time: 0,
        completed_at: null,
      }));

      const { data: insertedAssignments, error: insertError } =
        await this.supabase
          .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
          .insert(relationshipData)
          .select();

      if (insertError) {
        console.error('Error creating homework assignments:', insertError);
        return { data: null, error: insertError };
      }

      console.log(
        `✅ Successfully initialized ${
          insertedAssignments?.length || 0
        } homework assignments for student`,
      );
      return { data: insertedAssignments, error: null };
    } catch (error) {
      console.error('Error initializing homework for student:', error);
      return { data: null, error };
    }
  }
}
